/**
 * The right-margin comment rail. Present on EVERY page, TipTap and raw-HTML alike.
 *
 * Why a margin and not a footer: comments used to sit under the article, which meant
 * on a long release page nobody ever saw them. Pages here are capped at 1080px on a
 * screen far wider, so the margin was empty anyway.
 *
 * It lives in the app chrome, OUTSIDE the sandboxed iframe that renders HTML docs. That
 * is not incidental — the iframe is an opaque origin with no access to the session, so
 * a rail drawn inside it could not call the API at all. Both content types get the same
 * rail because the rail never touches content.
 *
 * A thread is one root comment plus its replies, never deeper. Resolve and assign act
 * on the thread; the API rejects them on a reply.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronRight,
  CornerDownRight,
  MessageSquare,
  Quote,
  Trash2,
  UserPlus,
} from 'lucide-react';

import { api } from '@/lib/api';
import type { Comment, CommentAnchor } from '@/lib/types';
import { useAuth } from '@/context/AuthContext';

export const RAIL_WIDTH = 340;

interface Thread {
  root: Comment;
  replies: Comment[];
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  if (mins < 60 * 24 * 7) return `${Math.round(mins / (60 * 24))}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** "roman@settheroom.com" -> "roman", so the rail stays readable at 340px. */
function shortName(email: string | null | undefined): string {
  if (!email) return '';
  return email.split('@')[0];
}

function initials(email: string): string {
  const n = shortName(email);
  return (n.slice(0, 2) || '?').toUpperCase();
}

export default function CommentRail({
  nodeId,
  pendingAnchor,
  onClearAnchor,
}: {
  nodeId: string;
  /** Quote captured by highlighting the page; attached to the next comment posted. */
  pendingAnchor?: CommentAnchor | null;
  onClearAnchor?: () => void;
}) {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[] | null>(null);
  const [roster, setRoster] = useState<Array<{ email: string; displayName: string }>>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setComments(await api.comments(nodeId));
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load comments');
    }
  }, [nodeId]);

  useEffect(() => {
    setComments(null);
    void load();
  }, [load]);

  useEffect(() => {
    // The roster is only needed for the assign menu, and it never changes mid-session.
    api.users().then(setRoster).catch(() => setRoster([]));
  }, []);

  const threads = useMemo<Thread[]>(() => {
    if (!comments) return [];
    const roots = comments.filter((c) => !c.parentId);
    return roots.map((root) => ({
      root,
      replies: comments.filter((c) => c.parentId === root.id),
    }));
  }, [comments]);

  const open = threads.filter((t) => !t.root.resolved);
  const resolved = threads.filter((t) => t.root.resolved);
  const shown = showResolved ? [...open, ...resolved] : open;

  /** Replace one comment in place — avoids a refetch flashing the whole rail. */
  const patchLocal = (next: Comment) =>
    setComments((prev) => (prev ?? []).map((c) => (c.id === next.id ? next : c)));

  async function act<T>(id: string, fn: () => Promise<T>) {
    setBusy(id);
    try {
      return await fn();
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Action failed');
      return null;
    } finally {
      setBusy(null);
    }
  }

  return (
    <aside
      className="hidden shrink-0 lg:block"
      style={{ width: RAIL_WIDTH }}
      aria-label="Comments"
    >
      <div className="sticky top-0 max-h-screen overflow-y-auto py-8 pr-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <MessageSquare size={14} />
            Comments
            {open.length > 0 && <span className="text-ink-faint">({open.length})</span>}
          </h2>
          {resolved.length > 0 && (
            <button
              type="button"
              onClick={() => setShowResolved((v) => !v)}
              className="text-xs text-ink-faint hover:text-ink"
            >
              {showResolved ? 'Hide' : 'Show'} resolved ({resolved.length})
            </button>
          )}
        </div>

        {loadError && (
          <p className="mt-3 rounded border border-border bg-surface px-3 py-2 text-xs text-ink-muted">
            {loadError}
          </p>
        )}

        <div className="mt-3 space-y-2">
          {comments === null && <p className="text-sm text-ink-faint">Loading…</p>}
          {comments !== null && shown.length === 0 && (
            <p className="text-sm text-ink-faint">
              {resolved.length > 0 ? 'Nothing open.' : 'No comments yet.'}
            </p>
          )}
          {shown.map((t) => (
            <ThreadCard
              key={t.root.id}
              thread={t}
              me={user?.email ?? ''}
              roster={roster}
              busy={busy === t.root.id}
              onResolve={async (resolved) => {
                const next = await act(t.root.id, () =>
                  api.updateComment(t.root.id, { resolved }),
                );
                if (next) patchLocal(next);
              }}
              onAssign={async (assignee) => {
                const next = await act(t.root.id, () =>
                  api.updateComment(t.root.id, { assignee }),
                );
                if (next) patchLocal(next);
              }}
              onReply={async (body) => {
                const created = await act(t.root.id, () =>
                  api.addComment({ nodeId, body, parentId: t.root.id }),
                );
                if (created) setComments((prev) => [...(prev ?? []), created]);
              }}
              onDelete={async (id) => {
                const done = await act(t.root.id, () => api.deleteComment(id));
                if (done) setComments((prev) => (prev ?? []).filter((c) => c.id !== id));
              }}
            />
          ))}
        </div>

        <NewThread
          anchor={pendingAnchor ?? null}
          onClearAnchor={onClearAnchor}
          onSubmit={async (body, assignee) => {
            const created = await api.addComment({
              nodeId,
              body,
              assignee,
              anchor: pendingAnchor ?? undefined,
            });
            setComments((prev) => [...(prev ?? []), created]);
            onClearAnchor?.();
          }}
          roster={roster}
        />
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------

function ThreadCard({
  thread,
  me,
  roster,
  busy,
  onResolve,
  onAssign,
  onReply,
  onDelete,
}: {
  thread: Thread;
  me: string;
  roster: Array<{ email: string; displayName: string }>;
  busy: boolean;
  onResolve: (resolved: boolean) => void | Promise<void>;
  onAssign: (assignee: string | null) => void | Promise<void>;
  onReply: (body: string) => void | Promise<void>;
  onDelete: (id: string) => void | Promise<void>;
}) {
  const { root, replies } = thread;
  // Collapsed by default once resolved — the point of resolving is to stop reading it.
  const [expanded, setExpanded] = useState(!root.resolved);
  const [replyText, setReplyText] = useState('');
  const [assigning, setAssigning] = useState(false);

  return (
    <div
      className={`rounded border border-border bg-surface p-3 transition-opacity ${
        root.resolved ? 'opacity-60' : ''
      } ${busy ? 'opacity-50' : ''}`}
    >
      {root.anchor && (
        <p className="mb-2 line-clamp-2 border-l-2 border-accent pl-2 text-[11px] italic text-ink-faint">
          {root.anchor.quote}
        </p>
      )}
      <div className="flex items-start gap-2">
        <span
          className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold"
          style={{ background: 'var(--accent)', color: 'var(--accent-ink)' }}
          title={root.author}
        >
          {initials(root.author)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-xs font-medium text-ink">{shortName(root.author)}</span>
            <span className="shrink-0 text-[10px] text-ink-faint">{fmtWhen(root.createdAt)}</span>
          </div>
          {expanded ? (
            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-ink-muted">{root.body}</p>
          ) : (
            <p className="mt-1 truncate text-sm text-ink-faint">{root.body}</p>
          )}
        </div>
      </div>

      {root.assignee && (
        <p className="mt-2 text-[11px] text-ink-faint">
          Assigned to <span className="font-medium text-ink-muted">{shortName(root.assignee)}</span>
        </p>
      )}
      {root.resolved && root.resolvedBy && (
        <p className="mt-1 text-[11px] text-ink-faint">
          Resolved by {shortName(root.resolvedBy)}
        </p>
      )}

      {expanded && replies.length > 0 && (
        <ul className="mt-2 space-y-2 border-l border-border pl-3">
          {replies.map((r) => (
            <li key={r.id}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-xs font-medium text-ink">{shortName(r.author)}</span>
                <span className="shrink-0 text-[10px] text-ink-faint">{fmtWhen(r.createdAt)}</span>
              </div>
              <p className="whitespace-pre-wrap break-words text-sm text-ink-muted">{r.body}</p>
              {r.author === me && (
                <button
                  type="button"
                  onClick={() => void onDelete(r.id)}
                  className="mt-0.5 text-[10px] text-ink-faint hover:text-ink"
                >
                  Delete
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        <button
          type="button"
          disabled={busy}
          onClick={() => void onResolve(!root.resolved)}
          className="inline-flex items-center gap-1 text-ink-faint hover:text-ink disabled:opacity-50"
        >
          <Check size={11} />
          {root.resolved ? 'Reopen' : 'Resolve'}
        </button>
        <button
          type="button"
          onClick={() => setAssigning((v) => !v)}
          className="inline-flex items-center gap-1 text-ink-faint hover:text-ink"
        >
          <UserPlus size={11} />
          {root.assignee ? 'Reassign' : 'Assign'}
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-1 text-ink-faint hover:text-ink"
        >
          <ChevronRight size={11} className={expanded ? 'rotate-90' : ''} />
          {expanded ? 'Collapse' : `Expand${replies.length ? ` (${replies.length})` : ''}`}
        </button>
        {root.author === me && replies.length === 0 && (
          <button
            type="button"
            onClick={() => void onDelete(root.id)}
            className="inline-flex items-center gap-1 text-ink-faint hover:text-ink"
          >
            <Trash2 size={11} />
            Delete
          </button>
        )}
      </div>

      {assigning && (
        <div className="mt-2 flex flex-wrap gap-1">
          {roster.map((u) => (
            <button
              key={u.email}
              type="button"
              onClick={() => {
                setAssigning(false);
                void onAssign(u.email === root.assignee ? null : u.email);
              }}
              className={`rounded border px-1.5 py-0.5 text-[11px] ${
                u.email === root.assignee
                  ? 'border-accent text-ink'
                  : 'border-border text-ink-muted hover:text-ink'
              }`}
            >
              {u.displayName}
            </button>
          ))}
          {root.assignee && (
            <button
              type="button"
              onClick={() => {
                setAssigning(false);
                void onAssign(null);
              }}
              className="rounded border border-border px-1.5 py-0.5 text-[11px] text-ink-faint hover:text-ink"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {expanded && !root.resolved && (
        <form
          className="mt-2 flex items-start gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            const body = replyText.trim();
            if (!body) return;
            setReplyText('');
            void onReply(body);
          }}
        >
          <CornerDownRight size={12} className="mt-2 shrink-0 text-ink-faint" />
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) e.currentTarget.form?.requestSubmit();
            }}
            rows={1}
            placeholder="Reply…"
            className="min-h-[28px] w-full resize-y rounded border border-border bg-surface-2 px-2 py-1 text-xs text-ink placeholder:text-ink-faint focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function NewThread({
  onSubmit,
  roster,
  anchor,
  onClearAnchor,
}: {
  onSubmit: (body: string, assignee: string | null) => Promise<void>;
  roster: Array<{ email: string; displayName: string }>;
  anchor: CommentAnchor | null;
  onClearAnchor?: () => void;
}) {
  const [body, setBody] = useState('');
  const [assignee, setAssignee] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLTextAreaElement>(null);

  // Highlighting text and clicking "Add comment" should land the cursor here — the
  // whole point of the gesture is to start typing straight away.
  useEffect(() => {
    if (anchor) boxRef.current?.focus();
  }, [anchor]);

  async function submit() {
    const text = body.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(text, assignee);
      setBody('');
      setAssignee(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      className="mt-3"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {anchor && (
        <div className="mb-1.5 flex items-start gap-1.5 rounded border-l-2 border-accent bg-surface-2 px-2 py-1.5">
          <Quote size={11} className="mt-0.5 shrink-0 text-ink-faint" />
          <p className="min-w-0 flex-1 line-clamp-3 text-[11px] italic text-ink-muted">
            {anchor.quote}
          </p>
          <button
            type="button"
            onClick={onClearAnchor}
            title="Comment on the page instead of this quote"
            className="shrink-0 text-[11px] leading-none text-ink-faint hover:text-ink"
          >
            ×
          </button>
        </div>
      )}
      <textarea
        ref={boxRef}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submit();
        }}
        rows={3}
        placeholder={anchor ? 'Comment on this quote…' : 'Add a comment…  (⌘/Ctrl + Enter to send)'}
        className="w-full resize-y rounded border border-border bg-surface px-2 py-1.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-1 focus:ring-accent"
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <select
          value={assignee ?? ''}
          onChange={(e) => setAssignee(e.target.value || null)}
          className="min-w-0 flex-1 rounded border border-border bg-surface px-1.5 py-1 text-[11px] text-ink-muted focus:outline-none"
        >
          <option value="">Unassigned</option>
          {roster.map((u) => (
            <option key={u.email} value={u.email}>
              {u.displayName}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={!body.trim() || submitting}
          className="shrink-0 rounded bg-accent px-2.5 py-1 text-xs font-medium text-accent-ink hover:bg-accent-hover disabled:opacity-50"
        >
          {submitting ? 'Posting…' : 'Comment'}
        </button>
      </div>
    </form>
  );
}
